<template>
  <div>
    <!-- Header Section -->
    <div class="row mb-4">
      <div class="col-md-8">
        <div class="d-flex align-items-center">
          <div class="bg-primary-light p-3 rounded-2 me-3">
            <vue-feather type="briefcase" class="d-flex justify-content-center align-content-center" stroke="#652aa3" size="24"></vue-feather>
          </div>
          <h3 class="mb-0 fw-bold">Departments Management</h3>
        </div>
      </div>
      <div class="col-md-4">
        <div class="d-flex justify-content-end">
          <button
            v-if="profile.role !== 'user' && profile.role !== 'admin'"
            class="btn btn-primary shadow-sm"
            @click="departmentModal = true; departmentMode = 'add'"
          >
            <vue-feather type="plus" size="18" class="me-1"></vue-feather>
            Add Department
          </button>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <Card cardClasses="shadow-sm border-0 h-100 mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div class="font-size-125 color-gray-600">
              <vue-feather type="list" stroke="#652aa3" size="18" class="me-1"></vue-feather>
              Departments ({{departments.length}})
            </div>
            <div class="bg-primary-light p-2 rounded-circle">
              <vue-feather type="briefcase" class="d-flex justify-content-center align-content-center" stroke="#652aa3" size="20"></vue-feather>
            </div>
          </div>

          <div class="table-responsive">
            <table class="table table-hover align-middle">
              <thead class="table-light">
                <tr>
                  <th scope="col" class="fw-semibold">Code</th>
                  <th scope="col" class="fw-semibold">Name</th>
                  <th scope="col" class="fw-semibold">Description</th>
                  <th scope="col" class="fw-semibold">Designations</th>
                  <th scope="col" class="fw-semibold">Created</th>
                  <th scope="col" class="text-end">Actions</th>
                </tr>
              </thead>
              <tbody v-if="!loading">
                <tr v-for="department in departments" :key="department.id" class="department-row">
                  <td>
                    <span class="badge bg-primary-light text-primary">{{ department.code }}</span>
                  </td>
                  <td class="fw-medium">{{ department.name }}</td>
                  <td>{{ department.description || 'Not specified' }}</td>
                  <td>
                    <span class="badge bg-success-light text-success">
                      {{ department.designations ? department.designations.length : 0 }}
                    </span>
                  </td>
                  <td>{{ formatDate(department.created_at) }}</td>
                  <td class="text-end">
                    <button
                      class="btn btn-sm btn-outline-info me-2"
                      @click="viewDepartment(department)"
                    >
                      <vue-feather type="eye" size="16"></vue-feather>
                      View
                    </button>
                    <button
                      v-if="profile.role !== 'user' && profile.role !== 'admin'"
                      class="btn btn-sm btn-outline-primary"
                      @click="departmentModal = true; departmentMode = 'edit'; populateDepartmentEdit(department)"
                    >
                      <vue-feather type="edit-2" size="16"></vue-feather>
                      Edit
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="loading" class="text-center py-4">
              <Spinner spinner-class="mb-1" />
            </div>
            <div v-if="!loading && departments.length === 0" class="text-center py-4">
              <div class="empty-state">
                <div class="bg-light p-4 rounded-circle mb-3 d-inline-block">
                  <vue-feather type="briefcase" size="32" stroke="#6c757d"></vue-feather>
                </div>
                <h5>No Departments Found</h5>
                <p class="text-muted">Start by adding your first department</p>
                <button
                  v-if="profile.role !== 'user' && profile.role !== 'admin'"
                  class="btn btn-primary"
                  @click="departmentModal = true; departmentMode = 'add'"
                >
                  <vue-feather type="plus" size="16" class="me-1"></vue-feather>
                  Add Department
                </button>
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-center mt-4">
            <button class="btn btn-outline-dark text-uppercase me-4 shadow-sm" @click="$router.go(-1)">
              <vue-feather type="arrow-left" size="16" class="me-1"></vue-feather>
              Back
            </button>
          </div>
        </Card>
      </div>
    </div>

    <!-- View Department Modal -->
    <Modal :open="viewDepartmentModal" @closed="viewDepartmentModal = false" dialog-size="modal-lg">
      <div class="modal-header border-0">
        <div class="d-flex align-items-center">
          <div class="bg-primary-light p-2 rounded-2 me-3">
            <vue-feather type="briefcase" stroke="#652aa3" size="20"></vue-feather>
          </div>
          <h5 class="modal-title m-0 fw-bold">Department Details</h5>
        </div>
        <button @click="viewDepartmentModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <div class="row mb-4" v-if="selectedDepartment">
          <div class="col-md-6 mb-3">
            <label class="text-muted small fw-semibold">Department Code</label>
            <div class="fw-medium">
              <span class="badge bg-primary-light text-primary">{{ selectedDepartment.code }}</span>
            </div>
          </div>
          <div class="col-md-6 mb-3">
            <label class="text-muted small fw-semibold">Department Name</label>
            <div class="fw-medium">{{ selectedDepartment.name }}</div>
          </div>
          <div class="col-md-6 mb-3">
            <label class="text-muted small fw-semibold">Description</label>
            <div class="fw-medium">{{ selectedDepartment.description || 'Not specified' }}</div>
          </div>
          <div class="col-md-6 mb-3">
            <label class="text-muted small fw-semibold">Created Date</label>
            <div class="fw-medium">{{ formatDate(selectedDepartment.created_at) }}</div>
          </div>
        </div>

        <hr class="my-4">

        <div class="designations-section">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="fw-bold mb-0">
              <vue-feather type="award" size="18" class="me-2" stroke="#652aa3"></vue-feather>
              Designations ({{ departmentDesignations.length }})
            </h6>
          </div>

          <div v-if="departmentDesignations.length > 0" class="table-responsive">
            <table class="table table-sm table-hover">
              <thead class="table-light">
                <tr>
                  <th scope="col" class="fw-semibold">Code</th>
                  <th scope="col" class="fw-semibold">Name</th>
                  <th scope="col" class="fw-semibold">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="designation in departmentDesignations" :key="designation.id">
                  <td>
                    <span class="badge bg-primary-light text-primary">{{ designation.code }}</span>
                  </td>
                  <td class="fw-medium">{{ designation.name }}</td>
                  <td>{{ designation.description || 'Not specified' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="text-center py-4">
            <div class="bg-light p-3 rounded-circle mb-2 d-inline-block">
              <vue-feather type="award" size="24" stroke="#6c757d"></vue-feather>
            </div>
            <p class="text-muted mb-0">No designations found for this department</p>
          </div>
        </div>
      </div>
      <div class="modal-footer border-0 pt-0">
        <button
          type="button"
          class="btn btn-outline-secondary"
          @click="viewDepartmentModal = false"
        >
          Close
        </button>
      </div>
    </Modal>

    <!-- Department Modal -->
    <Modal :open="departmentModal" @closed="departmentModal = false">
      <div class="modal-header border-0">
        <div class="d-flex align-items-center">
          <h5 class="modal-title m-0 fw-bold" v-if="departmentMode === 'add'">Add Department</h5>
          <h5 class="modal-title m-0 fw-bold" v-else>Edit Department</h5>
        </div>
        <button @click="departmentModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="">
          <div class="row g-3">
            <div class="col-md-12 mb-2 h5">
              <label for="code" class="form-label fw-bolder">Department Code</label>
              <div class="input-group">
                <input
                  type="text"
                  id="code"
                  class="form-control"
                  placeholder="Department code"
                  v-model="department.code"
                  readonly
                >
              </div>
              <small class="text-muted fst-italic font-size-75">Code is automatically generated</small>
            </div>
            <div class="col-md-12 mb-2 h5">
              <label for="name" class="form-label fw-bolder">Department Name</label>
              <div class="input-group">
                <input
                  type="text"
                  id="name"
                  class="form-control"
                  placeholder="Enter department name"
                  v-model="department.name"
                >
              </div>
            </div>
            <div class="col-md-12 mb-2 h5">
              <label for="description" class="form-label fw-bolder">Description <span class="text-muted">(optional)</span></label>
              <div class="input-group">
                <input
                  type="text"
                  id="description"
                  class="form-control"
                  placeholder="Enter department description"
                  v-model="department.description"
                >
              </div>
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer border-0 pt-0">
        <button
          type="button"
          class="btn btn-outline-secondary"
          data-dismiss="modal"
          @click="departmentModal = false"
        >
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-primary"
          v-if="departmentMode === 'add'"
          @click="addDepartment($event)"
        >
          Save Department
        </button>
        <button
          type="button"
          class="btn btn-primary"
          v-else
          @click="updateDepartment($event, department.id)"
        >
          Update Department
        </button>
      </div>
    </Modal>
  </div>
</template>

<script>
import departmentService from "@/api/departmentService";
import formatDate from "../../utils/formatDate";
import {decrypt} from "@/utils/crypto";
import loadingButton from "@/utils/loadingButton";
import VueFeather from 'vue-feather';

export default {
  name: "Departments",
  components: { VueFeather },

  data(){
    return {
      profile: {},
      loading: true,
      departments: [],
      departmentMode: 'add',
      departmentModal: false,
      viewDepartmentModal: false,
      selectedDepartment: null,
      department: {
        code: '',
        name: '',
        description: '',
      },
      nextNo: {},
    }
  },

  computed: {
    departmentDesignations() {
      if (!this.selectedDepartment || !this.selectedDepartment.designations) return []
      return this.selectedDepartment.designations
    }
  },

  methods: {
    formatDate,
    async getDepartments() {
      this.loading = true
      try {
        this.departments = await departmentService.getDepartments()
        this.nextNo = await departmentService.getDepartmentNextNo()
        this.department.code = this.nextNo.next
      } catch (error) {
        console.error("Error fetching departments:", error)
      } finally {
        this.loading = false
      }
    },

    viewDepartment(department) {
      this.selectedDepartment = department
      this.viewDepartmentModal = true
    },

    populateDepartmentEdit(department) {
      this.department = { ...department }
    },

    async addDepartment(e) {
      if (!this.department.name) {
        return
      }

      loadingButton(e)
      try {
        let response = await departmentService.createDepartment(this.department)
        if (response) {
          this.departmentModal = false
          await this.getDepartments()
        }
      } catch (error) {
        console.error("Error adding department:", error)
      } finally {
        loadingButton(e, false)
      }
    },

    async updateDepartment(e, id) {
      if (!this.department.name) {
        return
      }

      loadingButton(e)
      try {
        let response = await departmentService.updateDepartment(this.department, id)
        if (response) {
          this.departmentModal = false
          await this.getDepartments()
        }
      } catch (error) {
        console.error("Error updating department:", error)
      } finally {
        loadingButton(e, false)
      }
    }
  },

  created: async function () {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    await this.getDepartments()
    if(this.$route.query.add)
      this.departmentModal = true
  },

  watch: {
    departmentModal: function () {
      if (!this.departmentModal) {
        this.department = {
          code: this.nextNo.next,
          name: '',
          description: '',
        }
      }
    },
  }
}
</script>

<style scoped>
.bg-primary-light {
  background-color: rgba(101, 42, 163, 0.1);
}

.text-primary {
  color: #652aa3 !important;
}

.bg-success-light {
  background-color: rgba(25, 135, 84, 0.1);
}

.text-success {
  color: #198754 !important;
}

.department-row {
  transition: all 0.2s ease;
}

.department-row:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.empty-state {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.font-size-100 {
  font-size: 1rem;
}

.font-size-125 {
  font-size: 1.25rem;
}

.font-size-75 {
  font-size: 0.75rem;
}

.color-gray-600 {
  color: #6c757d;
}

.card {
  transition: all 0.3s ease;
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
}

.btn {
  transition: all 0.2s ease;
}

.btn:hover {
  transform: translateY(-2px);
}

.input-group {
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  border-radius: 0.25rem;
}

.input-group:focus-within {
  box-shadow: 0 2px 10px rgba(101, 42, 163, 0.15);
}
</style>
